import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import ToggleSwitch from "../../components/ToggleSwitch/ToggleSwitch";
import ReactSlidingPane from "react-sliding-pane";
import "react-sliding-pane/dist/react-sliding-pane.css";
import { useEnableLopSettings } from "../../queries/payrollQuries";
import { toast } from "react-toastify";
import { closeIcon } from "../../Icons/Icons";
import { useGetEmployeeTypeList } from "../../queries/employeeQueries";
import { DatePicker } from "antd";
import FormFeildInfo from "../../components/FormFeildInfo";
import dayjs from "dayjs";
import {
  calculationMethodOption,
  calculationBasisOptions,
} from "../../utils/commonSelectBoxOptions/commonSelectBoxOptions";

const AddOrEditLopSettings = ({ closePane, editData }) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...editData,
      include_half_day: editData?.include_half_day ?? false,
      include_weekends: editData?.include_weekends ?? false,
      hide_in_payslip: editData?.hide_in_payslip ?? false,
      is_active: editData?.is_active ?? true,
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedEmployeeTypes, setSelectedEmployeeTypes] = useState(
    Array.isArray(editData?.employee_type_ids) ? editData.employee_type_ids : []
  );
  const [infoVisible, setInfoVisible] = useState({});

  const { data, isLoading: employeeTypeListLoading } = useGetEmployeeTypeList();
  const enableLOPSettings = useEnableLopSettings();
  const employeeTypeListOptions = data?.result || [];

  const handleFocus = (field) =>
    setInfoVisible((prev) => ({ ...prev, [field]: true }));
  const handleBlur = (field) =>
    setInfoVisible((prev) => ({ ...prev, [field]: false }));

  const getErrorMessage = (field) => errors[field]?.message || "";

  const handleEmployeeTypeChange = (id) => {
    setSelectedEmployeeTypes((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const onSubmit = (data) => {
    if (isSubmitting) return;

    if (selectedEmployeeTypes.length === 0) {
      toast.error("Please select at least one employee type.");
      return;
    }

    setIsSubmitting(true);

    const payload = {
      id: editData?.id,
      effective_from: dayjs(data?.effective_from).format("YYYY-MM-DD"),
      calculation_method: data?.calculation_method,
      frequecy_of_calucation: data?.frequecy_of_calucation,
      include_half_day: Boolean(data?.include_half_day),
      include_weekends: Boolean(data?.include_weekends),
      is_active: Boolean(data?.is_active),
      hide_in_payslip: Boolean(data.hide_in_payslip),
      applicable_for: selectedEmployeeTypes,
      description: data.description,
    };

    enableLOPSettings.mutate(payload, {
      onSuccess: (response) => {
        toast.success(response.message || "LOP Settings saved successfully");
        closePane();
      },
      onError: (error) => {
        const errorMessage =
          error?.response?.data?.message || "Error saving LOP Settings";
        toast.error(errorMessage);
      },
      onSettled: () => {
        setIsSubmitting(false);
      },
    });
  };

  return (
    <ReactSlidingPane isOpen onRequestClose={closePane} width="450px">
      <div className="flex justify-between items-center px-5 border-b py-3">
        <h1 className="font-medium">
          {editData ? "Edit" : "Add"} LOP Settings
        </h1>
        <button onClick={closePane}>{closeIcon}</button>
      </div>
      <div className="p-4">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Calculation Method */}
          <div className="mb-4">
            <label className="block text-sm font-medium">Calculation Method</label>
            <Controller
              name="calculation_method"
              control={control}
              rules={{ required: "Calculation method is required" }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={calculationMethodOption}
                  value={calculationMethodOption.find((opt) => opt.value === field.value)}
                  onChange={(selected) => field.onChange(selected?.value)}
                  placeholder="Select calculation method"
                  onFocus={() => handleFocus("calculation_method")}
                  onBlur={() => handleBlur("calculation_method")}
                />
              )}
            />
            {infoVisible.calculation_method && (
              <FormFeildInfo info="Provide a calculation method." />
            )}
            <p className="text-red-500 text-xs">{getErrorMessage("calculation_method")}</p>
          </div>

          {/* Frequency of Calculation */}
          <div className="mb-4">
            <label className="block text-sm font-medium">Frequency of Calculation</label>
            <Controller
              name="frequecy_of_calucation"
              control={control}
              rules={{ required: "Frequency is required" }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={calculationBasisOptions}
                  value={calculationBasisOptions.find((opt) => opt.value === field.value)}
                  onChange={(selected) => field.onChange(selected?.value)}
                  placeholder="Select frequency"
                  onFocus={() => handleFocus("frequecy_of_calucation")}
                  onBlur={() => handleBlur("frequecy_of_calucation")}
                />
              )}
            />
            {infoVisible.frequecy_of_calucation && (
              <FormFeildInfo info="Provide a frequency of calculation." />
            )}
            <p className="text-red-500 text-xs">{getErrorMessage("frequecy_of_calucation")}</p>
          </div>

          {/* Effective From */}
          <div className="mb-4">
            <label className="block text-sm font-medium">Effective Date</label>
            <Controller
              name="effective_from"
              control={control}
              rules={{ required: "Effective date is required" }}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(date) => field.onChange(date)}
                  onFocus={() => handleFocus("effective_from")}
                  onBlur={() => handleBlur("effective_from")}
                />
              )}
            />
            <p className="text-red-500 text-xs">{getErrorMessage("effective_from")}</p>
          </div>

          {/* Toggles */}
          <div className="mb-4 flex items-center justify-between">
            <label className="block text-sm font-medium">Include Half Days</label>
            <ToggleSwitch
              checked={watch("include_half_day")}
              getToggleState={(val) => setValue("include_half_day", val)}
            />
          </div>

          <div className="mb-4 flex items-center justify-between">
            <label className="block text-sm font-medium">Include Weekends</label>
            <ToggleSwitch
              checked={watch("include_weekends")}
              getToggleState={(val) => setValue("include_weekends", val)}
            />
          </div>

          <div className="mb-4 flex items-center justify-between">
            <label className="block text-sm font-medium">Hide In Payslip</label>
            <ToggleSwitch
              checked={watch("hide_in_payslip")}
              getToggleState={(val) => setValue("hide_in_payslip", val)}
            />
          </div>

          <div className="mb-4 flex items-center justify-between">
            <label className="block text-sm font-medium">Is Active</label>
            <ToggleSwitch
              checked={watch("is_active")}
              getToggleState={(val) => setValue("is_active", val)}
            />
          </div>

          {/* Employee Types */}
          <div className="mb-4">
            <label className="block text-sm font-medium">Applicable For</label>
            {employeeTypeListLoading ? (
              <p>Loading employee types...</p>
            ) : (
              <div className="flex flex-wrap gap-4 mt-2">
                {employeeTypeListOptions.map((option) => (
                  <div key={option.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      value={option.id}
                      checked={selectedEmployeeTypes.includes(option.id)}
                      onChange={() => handleEmployeeTypeChange(option.id)}
                    />
                    <label>{option.type_label}</label>
                  </div>
                ))}
              </div>
            )}
            <p className="text-red-500 text-xs">
              {selectedEmployeeTypes.length === 0 &&
                getErrorMessage("applicable_for")}
            </p>
          </div>

          {/* Description */}
          <div className="mb-4">
            <label className="block text-sm font-medium">Description</label>
            <textarea
              className="border p-2 w-full"
              {...register("description", {
                required: "Description is required",
              })}
              onFocus={() => handleFocus("description")}
              onBlur={() => handleBlur("description")}
            />
            {infoVisible.description && (
              <FormFeildInfo info="Provide a short description of this LOP rule." />
            )}
            <p className="text-red-500 text-xs">
              {getErrorMessage("description")}
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-4">
            <button
              type="button"
              className="bg-gray-500 text-white px-4 py-2 rounded"
              onClick={closePane}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-4 py-2 rounded text-white transition ${
                isSubmitting || enableLOPSettings.isLoading
                  ? "bg-blue-300 cursor-not-allowed"
                  : "bg-blue-500 hover:bg-blue-600"
              }`}
              disabled={isSubmitting || enableLOPSettings.isLoading}
            >
              {isSubmitting || enableLOPSettings.isLoading
                ? editData
                  ? "Updating..."
                  : "Adding..."
                : editData
                ? "Update"
                : "Add"}{" "}
              LOP Settings
            </button>
          </div>
        </form>
      </div>
    </ReactSlidingPane>
  );
};

export default AddOrEditLopSettings;
